import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { relatoriosGlobais, gerarPcaDocx } from '../utils/relatoriosGlobais';
import { isAdminBase, isAdminAuthorized } from '../../utils/userUtils';
import LoadingModal from './modals/LoadingModal';

const Sidebar = ({ isSidebarOpen, toggleSidebar, sidebarRef }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [openMenu, setOpenMenu] = useState(null);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [user, setUser] = useState(null);
  const [usuarioBind, setUsuarioBind] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) setUser(JSON.parse(userData));
    const bindData = localStorage.getItem('usuarioBind');
    if (bindData) setUsuarioBind(JSON.parse(bindData));
  }, []);

  const toggleMenu = (menu) => {
    if (!isSidebarOpen) {
      toggleSidebar();
    }
    setOpenMenu(openMenu === menu ? null : menu);
    setOpenSubmenu(null);
  };

  const isActive = (path) => location.pathname === path;

  const isCadastroActive = () => [
    '/clientes', '/usuarios', '/unidades_medidas', '/tipos_objetos',
    '/requisitantes', '/responsabilidades', '/upload_imagem', '/margens_config',
    '/catmat_import', '/catmat_um_import', '/catser_import', '/catser_um_import',
  ].some(p => location.pathname === p);

  const isImportacoesActive = () => [
    '/catmat_import', '/catmat_um_import', '/catser_import', '/catser_um_import',
  ].some(p => location.pathname === p);

  const isDfdActive = () => ['/dfds', '/dfd_itens', '/dfd_lotes', '/dfd_lote_itens']
    .some(p => location.pathname === p || location.pathname.startsWith(p));

  const isPcaActive = () => ['/pca', '/pca_consolidado']
    .some(p => location.pathname === p || location.pathname.startsWith(p));

  const isRelatoriosActive = () => relatoriosGlobais.some(r =>
    location.pathname === `/${r.slug}` || location.pathname.startsWith(`/${r.slug}`)
  );

  useEffect(() => {
    if (isImportacoesActive()) { setOpenMenu('cadastro'); setOpenSubmenu('importacoes'); }
    else if (isCadastroActive()) setOpenMenu('cadastro');
    else if (isPcaActive()) setOpenMenu('pca');
    else if (isRelatoriosActive()) setOpenMenu('relatorios');
  }, [location.pathname]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (!isSidebarOpen) return;
      if (window.innerWidth >= 992) return;
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        toggleSidebar();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [sidebarRef, toggleSidebar, isSidebarOpen]);

  const hasPcaAccess = usuarioBind && (
    (usuarioBind.tipo === 'ADMIN' && user?.role === 'ADMIN') ||
    user?.role === 'USER' || usuarioBind.tipo === 'USER'
  );

  const handleReportAction = async (action, navigate) => {
    if (typeof action !== 'function') return;
    setLoading(true);
    try {
      await action(navigate);
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
    } finally {
      setLoading(false);
    }
  };

  const sidebarStyle = {
    width: isMobile ? (isSidebarOpen ? '220px' : '0px') : (isSidebarOpen ? '220px' : '40px'),
    transition: 'width 0.3s ease-in-out',
    background: 'linear-gradient(90deg, #0b3383, #0b3383)',
    padding: isMobile && !isSidebarOpen ? '0' : '5px',
    height: 'calc(100vh - 47px)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    top: '40px',
    left: 0,
    zIndex: 1000,
    boxShadow: isMobile && !isSidebarOpen ? 'none' : '2px 0 10px rgba(0, 0, 0, 0.2)',
  };

  const linkStyle = (active) => ({
    color: '#fff',
    fontSize: '0.85rem',
    padding: '0.35rem 0.5rem',
    borderRadius: '0.3rem',
    backgroundColor: active ? 'rgba(255,255,255,0.15)' : 'transparent',
    display: 'flex',
    alignItems: 'center',
    textDecoration: 'none',
  });

  const submenuStyle = {
    paddingLeft: '1rem',
    listStyle: 'none',
  };

  const tooltipProps = (label) => !isSidebarOpen ? { title: label } : {};

  return (
    <div ref={sidebarRef} className={`sidebar ${isSidebarOpen ? 'open' : 'closed'}`} style={sidebarStyle}>
      <LoadingModal loading={loading} />
      <button className="btn mb-1 text-white" onClick={toggleSidebar} style={{ fontSize: '0.85rem', padding: '0.35rem 0.5rem', width: '100%', display: 'flex', alignItems: 'center' }}>
        {isSidebarOpen ? <i className="bi bi-x-lg"></i> : <i className="bi bi-list"></i>}
      </button>

      <ul className="list-unstyled mb-0" style={{ flex: '1 1 auto', overflowY: 'auto' }}>
        {/* Home */}
        <li className="mb-1">
          <Link to="/home" style={linkStyle(isActive('/home'))} {...tooltipProps('Home')}>
            <i className="bi bi-house me-2"></i>
            <span style={{ display: isSidebarOpen ? 'inline' : 'none' }}>Home</span>
          </Link>
        </li>

        {/* Cadastro */}
        {isAdminAuthorized(user, usuarioBind) && (
          <li className="mb-1">
            <button
              onClick={() => toggleMenu('cadastro')}
              {...tooltipProps('Cadastro')}
              style={{ ...linkStyle(isCadastroActive()), width: '100%', border: 'none', textAlign: 'left', cursor: 'pointer', background: isCadastroActive() ? 'rgba(255,255,255,0.15)' : 'transparent' }}
            >
              <i className="bi bi-folder2 me-2"></i>
              <span style={{ display: isSidebarOpen ? 'inline' : 'none', flex: 1 }}>Cadastro</span>
              {isSidebarOpen && <i className={`bi bi-chevron-${openMenu === 'cadastro' ? 'down' : 'right'} small`}></i>}
            </button>
            {openMenu === 'cadastro' && isSidebarOpen && (
              <ul className="list-unstyled" style={submenuStyle}>
                {isAdminAuthorized(user, usuarioBind) && (
                  <li><Link to="/clientes" style={linkStyle(isActive('/clientes'))}><i className="bi bi-building me-2"></i>Entidades</Link></li>
                )}
                {isAdminAuthorized(user, usuarioBind) && (
                  <li><Link to="/usuarios" style={linkStyle(isActive('/usuarios'))}><i className="bi bi-people me-2"></i>Usuários</Link></li>
                )}
                {isAdminAuthorized(user, usuarioBind) && (
                  <li><Link to="/unidades_medidas" style={linkStyle(isActive('/unidades_medidas'))}><i className="bi bi-rulers me-2"></i>Unidades de Medida</Link></li>
                )}
                {isAdminAuthorized(user, usuarioBind) && (
                  <li><Link to="/tipos_objetos" style={linkStyle(isActive('/tipos_objetos'))}><i className="bi bi-tags me-2"></i>Tipos de Objeto</Link></li>
                )}
                <li><Link to="/requisitantes" style={linkStyle(isActive('/requisitantes'))}><i className="bi bi-person-badge me-2"></i>Requisitantes</Link></li>
                <li><Link to="/responsabilidades" style={linkStyle(isActive('/responsabilidades'))}><i className="bi bi-person-check me-2"></i>Responsabilidades</Link></li>
                {isAdminBase(user, usuarioBind) && (
                  <li><Link to="/upload_imagem" style={linkStyle(isActive('/upload_imagem'))}><i className="bi bi-image me-2"></i>Imagens</Link></li>
                )}
                {isAdminBase(user, usuarioBind) && (
                  <li><Link to="/margens_config" style={linkStyle(isActive('/margens_config'))}><i className="bi bi-rulers me-2"></i>Margens</Link></li>
                )}
                {isAdminAuthorized(user, usuarioBind) && (
                  <>
                    <li>
                      <button
                        onClick={() => setOpenSubmenu(openSubmenu === 'importacoes' ? null : 'importacoes')}
                        style={{ ...linkStyle(isImportacoesActive()), width: '100%', border: 'none', textAlign: 'left', cursor: 'pointer', background: 'transparent', fontSize: '0.85rem' }}
                      >
                        <i className="bi bi-download me-2"></i>
                        <span style={{ flex: 1 }}>Importações</span>
                        {isSidebarOpen && <i className={`bi bi-chevron-${openSubmenu === 'importacoes' ? 'down' : 'right'} small`}></i>}
                      </button>
                      {openSubmenu === 'importacoes' && (
                        <ul className="list-unstyled" style={{ paddingLeft: '1rem' }}>
                          <li><Link to="/catmat_import" style={linkStyle(isActive('/catmat_import'))}><i className="bi bi-download me-2"></i>CATMAT</Link></li>
                          <li><Link to="/catmat_um_import" style={linkStyle(isActive('/catmat_um_import'))}><i className="bi bi-download me-2"></i>CATMAT UM</Link></li>
                          <li><Link to="/catser_import" style={linkStyle(isActive('/catser_import'))}><i className="bi bi-download me-2"></i>CATSER</Link></li>
                          <li><Link to="/catser_um_import" style={linkStyle(isActive('/catser_um_import'))}><i className="bi bi-download me-2"></i>CATSER UM</Link></li>
                        </ul>
                      )}
                    </li>
                  </>
                )}
              </ul>
            )}
          </li>
        )}

        {/* DFD */}
        <li className="mb-1">
          <Link to="/dfds" style={linkStyle(isDfdActive())} {...tooltipProps('DFD')}>
            <i className="bi bi-file-earmark-text me-2"></i>
            <span style={{ display: isSidebarOpen ? 'inline' : 'none' }}>DFD</span>
          </Link>
        </li>

        {/* Aprova DFD */}
        {isAdminBase(user, usuarioBind) && (
          <li className="mb-1">
            <Link to="/aprova_dfd" style={linkStyle(isActive('/aprova_dfd'))} {...tooltipProps('Aprova DFD')}>
              <i className="bi bi-check-circle me-2"></i>
              <span style={{ display: isSidebarOpen ? 'inline' : 'none' }}>Aprova DFD</span>
            </Link>
          </li>
        )}

        {/* PCA */}
        {hasPcaAccess && (
          <li className="mb-1">
            <button
              onClick={() => toggleMenu('pca')}
              {...tooltipProps('PCA')}
              style={{ ...linkStyle(isPcaActive()), width: '100%', border: 'none', textAlign: 'left', cursor: 'pointer', background: isPcaActive() ? 'rgba(255,255,255,0.15)' : 'transparent' }}
            >
              <i className="bi bi-file-earmark-text me-2"></i>
              <span style={{ display: isSidebarOpen ? 'inline' : 'none', flex: 1 }}>PCA</span>
              {isSidebarOpen && <i className={`bi bi-chevron-${openMenu === 'pca' ? 'down' : 'right'} small`}></i>}
            </button>
            {openMenu === 'pca' && isSidebarOpen && (
              <ul className="list-unstyled" style={submenuStyle}>
                <li><Link to="/pca" style={linkStyle(isActive('/pca'))}><i className="bi bi-file-earmark-text me-2"></i>PCA</Link></li>
                <li><Link to="/pca_consolidado" style={linkStyle(isActive('/pca_consolidado'))}><i className="bi bi-collection me-2"></i>Consolidação PCA</Link></li>
              </ul>
            )}
          </li>
        )}

        {/* Relatórios */}
        {hasPcaAccess && relatoriosGlobais.length > 0 && (
          <li className="mb-1">
            <button
              onClick={() => toggleMenu('relatorios')}
              {...tooltipProps('Relatórios')}
              style={{ ...linkStyle(isRelatoriosActive()), width: '100%', border: 'none', textAlign: 'left', cursor: 'pointer', background: isRelatoriosActive() ? 'rgba(255,255,255,0.15)' : 'transparent' }}
            >
              <i className="bi bi-file-earmark-pdf me-2"></i>
              <span style={{ display: isSidebarOpen ? 'inline' : 'none', flex: 1 }}>Relatórios</span>
              {isSidebarOpen && <i className={`bi bi-chevron-${openMenu === 'relatorios' ? 'down' : 'right'} small`}></i>}
            </button>
            {openMenu === 'relatorios' && isSidebarOpen && (
              <ul className="list-unstyled" style={submenuStyle}>
                {relatoriosGlobais.filter(r => !r.hidden).map((relatorio, index) =>
                  relatorio.submenu ? (
                    <li key={index}>
                      <button
                        onClick={() => setOpenSubmenu(openSubmenu === index ? null : index)}
                        style={{ ...linkStyle(false), width: '100%', border: 'none', textAlign: 'left', cursor: 'pointer', background: 'transparent' }}
                      >
                        <i className={`${relatorio.icon} me-2`}></i>
                        <span style={{ flex: 1, textAlign: 'left' }}>{relatorio.label}</span>
                        <i className={`bi bi-chevron-${openSubmenu === index ? 'down' : 'right'} small`}></i>
                      </button>
                      {openSubmenu === index && (
                        <ul className="list-unstyled" style={{ paddingLeft: '1rem' }}>
                          {relatorio.submenu.map((sub, subIndex) =>
                            sub.submenu ? (
                              <li key={subIndex}>
                                <button
                                  onClick={() => setOpenSubmenu(openSubmenu === `${index}-${subIndex}` ? null : `${index}-${subIndex}`)}
                                  style={{ ...linkStyle(false), width: '100%', border: 'none', textAlign: 'left', cursor: 'pointer', background: 'transparent', fontSize: '0.8rem' }}
                                >
                                  <i className={`${sub.icon} me-2`}></i>
                                  <span style={{ flex: 1, textAlign: 'left' }}>{sub.label}</span>
                                </button>
                              </li>
                            ) : (
                              <li key={subIndex}>
                                <button
                                  onClick={() => { setOpenMenu(null); setOpenSubmenu(null); if (sub.action) handleReportAction(sub.action, navigate); else if (relatorio.action) handleReportAction(relatorio.action, navigate); }}
                                  style={{ ...linkStyle(false), width: '100%', border: 'none', textAlign: 'left', cursor: 'pointer', background: 'transparent', fontSize: '0.8rem' }}
                                >
                                  <i className={`${sub.icon} me-2`}></i>
                                  {sub.label}
                                </button>
                              </li>
                            )
                          )}
                        </ul>
                      )}
                    </li>
                  ) : (
                    <li key={index}>
                      <button
                        onClick={() => { setOpenMenu(null); handleReportAction(relatorio.action, navigate); }}
                        style={{ ...linkStyle(false), width: '100%', border: 'none', textAlign: 'left', cursor: 'pointer', background: 'transparent' }}
                      >
                        <i className={`${relatorio.icon} me-2`}></i>
                        {relatorio.label}
                      </button>
                    </li>
                  )
                )}
              </ul>
            )}
          </li>
        )}

        {/* PNCP */}
        {hasPcaAccess && (
          <li className="mb-1">
            <Link to="/pncp" style={linkStyle(isActive('/pncp'))} {...tooltipProps('PNCP')}>
              <i className="bi bi-globe me-2"></i>
              <span style={{ display: isSidebarOpen ? 'inline' : 'none' }}>PNCP</span>
            </Link>
          </li>
        )}
      </ul>
    </div>
  );
};

export default Sidebar;
